// versionCheck.js — eliminates per-user version skew ("some see X, some see Y").
//
// The frontend build is stamped with its git commit (REACT_APP_GIT_COMMIT, injected
// at build time from RAILWAY_GIT_COMMIT_SHA). We compare that to the server's live
// commit (/api/version → gitCommit, same source). If they differ, this browser is
// running a STALE bundle.
//
// Instead of hard-reloading the page out from under the user, we surface a calm,
// dismissible "new version available" banner with a Refresh button. The user decides
// when to reload — no work lost mid-task, no jarring auto-refresh on every deploy.
//
// Safe by construction:
//   • If the build isn't stamped (local/dev), BUILD_COMMIT is empty → no-op.
//   • The banner is shown at most ONCE per distinct server commit (a newer deploy
//     re-shows it); never spams on the 5-min poll.
//   • Any fetch/parse failure (offline, cross-domain SPA-fallback HTML) is ignored.

const BUILD_COMMIT = (process.env.REACT_APP_GIT_COMMIT || '').trim();
const API_BASE = process.env.REACT_APP_API_URL || '';
const BANNER_ID = 'aone-update-banner';

// Remember which server commits we've already prompted for, so the periodic poll
// doesn't re-open the banner every cycle. sessionStorage survives reloads; the
// in-memory Set covers browsers where storage is blocked (private mode).
const shownCommits = new Set();
function alreadyShown(commit) {
  if (shownCommits.has(commit)) return true;
  try { return sessionStorage.getItem(`aone-update-shown-${commit}`) === '1'; } catch { return false; }
}
function markShown(commit) {
  shownCommits.add(commit);
  try { sessionStorage.setItem(`aone-update-shown-${commit}`, '1'); } catch { /* storage blocked */ }
}

async function reloadToLatest() {
  // Pull the new service-worker/app-shell first so the reload lands on the new
  // bundle rather than a cached one.
  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) await reg.update();
    }
  } catch { /* ignore */ }
  window.location.reload();
}

function showUpdateBanner() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BANNER_ID)) return;          // already on screen

  const bar = document.createElement('div');
  bar.id = BANNER_ID;
  bar.setAttribute('role', 'status');
  bar.setAttribute('aria-live', 'polite');
  // Top-right, compact. It used to sit bottom-centre, where it covered the
  // BOM footer, the Save bar and every page's totals row (2026-09-15).
  bar.style.cssText = [
    'position:fixed', 'right:16px', 'top:calc(64px + env(safe-area-inset-top, 0px))',
    'z-index:2147483647', 'display:flex', 'align-items:center', 'gap:10px',
    'max-width:calc(100vw - 32px)', 'padding:8px 8px 8px 14px',
    'background:#1f2733', 'color:#ffffff', 'border-radius:10px',
    'box-shadow:0 8px 28px rgba(0,0,0,0.28)',
    "font-family:'Segoe UI',-apple-system,Helvetica,Arial,sans-serif", 'font-size:13px',
  ].join(';');

  const msg = document.createElement('span');
  msg.textContent = 'New version available';
  msg.style.cssText = 'font-weight:500;white-space:nowrap;';

  const refresh = document.createElement('button');
  refresh.textContent = 'Refresh';
  refresh.style.cssText = [
    'background:#A66A2C', 'color:#ffffff', 'border:none', 'border-radius:8px',
    'padding:6px 12px', 'font-size:13px', 'font-weight:600', 'cursor:pointer',
    "font-family:inherit",
  ].join(';');
  refresh.addEventListener('click', reloadToLatest);

  const dismiss = document.createElement('button');
  dismiss.setAttribute('aria-label', 'Dismiss');
  dismiss.textContent = '✕';
  dismiss.style.cssText = [
    'background:transparent', 'color:#aeb6c2', 'border:none', 'cursor:pointer',
    'font-size:15px', 'line-height:1', 'padding:6px',
  ].join(';');
  dismiss.addEventListener('click', () => bar.remove());

  bar.append(msg, refresh, dismiss);
  document.body.appendChild(bar);
}

async function checkVersion() {
  if (!BUILD_COMMIT) return;                              // unstamped build — skip
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  let serverCommit;
  try {
    const r = await fetch(`${API_BASE}/api/version`, { cache: 'no-store' });
    if (!r.ok) return;
    serverCommit = (await r.json()).gitCommit;
  } catch {
    return;                                               // offline / non-JSON — ignore
  }
  if (!serverCommit || serverCommit === BUILD_COMMIT) return;
  if (alreadyShown(serverCommit)) return;                 // prompted already for this build
  markShown(serverCommit);
  showUpdateBanner();
}

export function installVersionCheck() {
  setTimeout(checkVersion, 4000);                         // shortly after first paint
  if (typeof window !== 'undefined') {
    window.addEventListener('focus', checkVersion);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') checkVersion();
    });
    setInterval(checkVersion, 5 * 60 * 1000);             // periodic safety net
  }
}
